import { defineStore, acceptHMRUpdate } from 'pinia'
import { GoogleMapsOverlay } from '@deck.gl/google-maps'
import { GeoJsonLayer, ScatterplotLayer } from '@deck.gl/layers'
import { useMaps } from './maps'
import { useYieldMap } from './yieldMap'
import { useAsAppliedMap } from './asAppliedMap'
import { hexToRgba } from '@/helpers/yieldMapBuckets'
import { useFieldLayersStore } from './fieldLayers'
import i18n from '@/i18n'

export const useIsoPreview = defineStore('isoPreview', {
    state: () => ({
        isUnmounted: false,
        overlay: null,
        boundaryLayer: null,
        gridLayer: null,
        visible: false,
        hoverPoint: null,
        selectedPointsKey: null,
        availablePointsKeys: [],
        previewPayload: null,
        legendDraggable: null,
        isoData: {
            taskId: null,
            file: null,
        },
        acceptedCoverage: 96,
        onlyPreviewAppMap: false,
    }),
    actions: {
        clearPreview(removeLegend = false) {
            const fls = useFieldLayersStore()

            if (this.overlay) {
                this.overlay.setMap(null)
            }
            this.overlay = null
            this.boundaryLayer = null
            this.gridLayer = null
            this.visible = false
            this.hoverPoint = null
            this.selectedPointsKey = null
            this.availablePointsKeys = []
            this.previewPayload = null
            this.isoData = {
                taskId: null,
                file: null,
            }
            this.onlyPreviewAppMap = false
            if (removeLegend && this.legendDraggable) {
                this.legendDraggable.disable()
            }
            fls.toggleFieldLayersVisibility(true)
        },
        setSelectedPointsKey(pointsKey) {
            if (!pointsKey || pointsKey === this.selectedPointsKey || this.isUnmounted) {
                return
            }
            this.selectedPointsKey = pointsKey
            if (this.previewPayload) {
                this.renderPreview(this.previewPayload)
            }
        },
        buildIsoTooltipHtml(point, unit, radiusLabel) {
            const coords = Array.isArray(point.coordinates) ? point.coordinates : []
            const lng = coords[0]
            const lat = coords[1]

            const swatch = point.fill
                ? `<span style="display:inline-block;width:10px;height:10px;border:1px solid #333;background:${point.fill};margin-right:6px;"></span>`
                : ''

            return `
                <div style="max-width:280px">
                    <div style="font-weight:600;margin-bottom:6px;">${i18n.global.t('ISO point')}</div>
                    <div>${swatch}<span style="opacity:.8">${i18n.global.t('Value')}</span>: <b>${point.value ?? '-'}</b></div>
                    <div><span style="opacity:.8">${i18n.global.t('Row/Col')}</span>: <b>${point.row ?? '-'}</b> / <b>${point.col ?? '-'}</b></div>
                    <div><span style="opacity:.8">${i18n.global.t('Lat/Lng')}</span>: <b>${lat ?? '-'}</b>, <b>${lng ?? '-'}</b></div>
                </div>
            `
        },

        renderPreview(previewPayload) {
            if (this.isUnmounted) {
                return { ok: false, message: 'Failed to show application map' }
            }

            const mapStore = useMaps()
            const fls = useFieldLayersStore()
            const aam = useAsAppliedMap()

            if (!mapStore.map || !mapStore.google || !previewPayload) {
                return { ok: false, message: 'Failed to show application map' }
            }

            const yieldMap = useYieldMap()
            if (yieldMap.yieldData) {
                yieldMap.clearYieldHeatmap()
            }
            aam.clearMap(true)

            if (Number(previewPayload?.grid_coverage?.covered_percent) === 0) {
                return { ok: false, message: 'Application map has 0% coverage for this field.' }
            }

            fls.toggleFieldLayersVisibility(false)

            this.previewPayload = previewPayload

            const boundaryFc = previewPayload?.field_border ? previewPayload.field_border : null

            const layers = []

            if (boundaryFc) {
                this.boundaryLayer = new GeoJsonLayer({
                    id: 'isoxml-boundary-layer',
                    data: boundaryFc,
                    filled: true,
                    stroked: true,
                    pickable: true,
                    getFillColor: (f) => hexToRgba(
                        f.properties?.fill || '#000000',
                        Number(f.properties?.['fill-opacity'] ?? 0)
                    ),
                    getLineColor: (f) => hexToRgba(
                        f.properties?.stroke || '#ff00ff',
                        Number(f.properties?.['stroke-opacity'] ?? 1)
                    ),
                    getLineWidth: Number(boundaryFc.features?.[0]?.properties?.['stroke-width'] ?? 2),
                    lineWidthUnits: 'pixels',
                })
                layers.push(this.boundaryLayer)
            }

            const gridFc = previewPayload?.grid_polygon ? previewPayload.grid_polygon : null

            if (gridFc) {
                this.gridLayer = new GeoJsonLayer({
                    id: 'isoxml-grid-polygon-layer',
                    data: gridFc,
                    filled: false,
                    stroked: true,
                    getLineColor: (f) => hexToRgba(
                        f.properties?.stroke || '#000000',
                        Number(f.properties?.['stroke-opacity'] ?? 1)
                    ),
                    getLineWidth: Number(gridFc.features?.[0]?.properties?.['stroke-width'] ?? 2),
                    lineWidthUnits: 'pixels',
                    pickable: true,
                })
                layers.push(this.gridLayer)
            }

            const pointsMap = previewPayload?.points || {}
            const pointsKeys = Object.keys(pointsMap)
            this.availablePointsKeys = pointsKeys

            if (pointsKeys.length > 0 && !this.selectedPointsKey) {
                this.selectedPointsKey = pointsKeys[0]
            }

            const pointsContainer =
                (this.selectedPointsKey && pointsMap[this.selectedPointsKey]) ||
                pointsMap[pointsKeys[0]] ||
                null
            const pointData = Array.isArray(pointsContainer?.points)
                ? pointsContainer.points
                : null

            const widthM = pointsContainer?.cell_size_m?.width_m
            const heightM = pointsContainer?.cell_size_m?.height_m
            const radiusMeters = Number.isFinite(widthM) && Number.isFinite(heightM)
                ? Math.min(widthM, heightM) * 0.45
                : 2.5
            const radiusLabel = Number.isFinite(radiusMeters) ? radiusMeters.toFixed(2) : null
            const unit = pointsContainer?.unit

            if (pointData?.length) {
                this.gridLayer = new ScatterplotLayer({
                    id: 'isoxml-points-layer',
                    data: pointData,
                    getPosition: (d) => d.coordinates,
                    getFillColor: (d) => hexToRgba(d.fill || '#888888', Number(d.fill_opacity ?? 0.75)),
                    getRadius: radiusMeters, // in meters
                    radiusUnits: 'meters',
                    // getRadius: 1,
                    // radiusUnits: 'pixels',
                    filled: true,
                    stroked: true,
                    getLineColor: (d) => hexToRgba(d.fill || '#888888', Number(d.stroke_opacity ?? 1)),
                    getLineWidth: 1,
                    lineWidthUnits: 'pixels',
                    pickable: true,
                    onHover: (info) => {
                        this.hoverPoint = info?.object || null
                    },
                    autoHighlight: true,
                    highlightColor: [255, 255, 255, 230],
                })
                layers.push(this.gridLayer)
            }

            if (!this.overlay) {
                this.overlay = new GoogleMapsOverlay({
                    layers,
                    controller: false,
                    style: { zIndex: 1000 },
                    getTooltip: ({ object }) => {
                        const p = object || this.hoverPoint
                        if (!p) return null

                        return {
                            html: this.buildIsoTooltipHtml(p, unit, radiusLabel)
                        }
                    },
                })
                this.overlay.setMap(mapStore.map)
            } else {
                this.overlay.setProps({
                    layers,
                    getTooltip: ({ object }) => {
                        const p = object || this.hoverPoint
                        if (!p) return null

                        return {
                            html: this.buildIsoTooltipHtml(p, unit, radiusLabel)
                        }
                    },
                })
            }

            const bounds = new mapStore.google.maps.LatLngBounds()

            const extendBoundsFromFeatureCollection = (fc) => {
                if (!fc?.features) return

                fc.features.forEach((feature) => {
                    const geom = feature?.geometry
                    if (!geom) return

                    if (geom.type === 'Polygon') {
                        geom.coordinates.forEach((ring) => {
                            ring.forEach(([lng, lat]) => bounds.extend({ lat, lng }))
                        })
                    } else if (geom.type === 'MultiPolygon') {
                        geom.coordinates.forEach((polygon) => {
                            polygon.forEach((ring) => {
                                ring.forEach(([lng, lat]) => bounds.extend({ lat, lng }))
                            })
                        })
                    }
                })
            }

            extendBoundsFromFeatureCollection(boundaryFc)

            if (pointData?.length) {
                pointData.forEach((p) => {
                    if (Array.isArray(p.coordinates) && p.coordinates.length === 2) {
                        bounds.extend({
                            lat: p.coordinates[1],
                            lng: p.coordinates[0],
                        })
                    }
                })
            }

            if (!bounds.isEmpty()) {
                mapStore.map.fitBounds(bounds)
            }

            this.visible = true
            return { ok: true, message: 'Application map shown successfully' }
        }
    }
})

if (import.meta.hot) {
    import.meta.hot.accept(acceptHMRUpdate(useIsoPreview, import.meta.hot))
}
